import { isEqual } from 'lodash';
import { createContext, memo, type ReactNode, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { type TemporalState, temporal } from 'zundo';
import { createStore, type StoreApi, useStore } from 'zustand';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type AnnotationCommentInputSlice,
  type CommentInputFocusHandler,
  type CommentInputFocusTarget,
  createAnnotationCommentInputSlice,
} from '~/components/v2/store/features/annotations/slices/createAnnotationCommentInputSlice';
import {
  type AnnotationDiscussionSlice,
  createAnnotationDiscussionSlice,
} from '~/components/v2/store/features/annotations/slices/createAnnotationDiscussionSlice';
import {
  type AnnotationHistorySlice,
  createAnnotationHistorySlice,
  MAX_ANNOTATION_HISTORY_SIZE,
} from '~/components/v2/store/features/annotations/slices/createAnnotationHistorySlice';
import {
  type AnnotationSessionSlice,
  createAnnotationSessionSlice,
} from '~/components/v2/store/features/annotations/slices/createAnnotationSessionSlice';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';

export type { CommentRange } from '~/components/v2/store/features/annotations/slices/createAnnotationDiscussionSlice';
export type { AnnotationTool } from '~/components/v2/store/features/annotations/slices/createAnnotationSessionSlice';

export type AnnotationV2StoreState = AnnotationSessionSlice &
  AnnotationHistorySlice &
  AnnotationDiscussionSlice &
  AnnotationCommentInputSlice;

/**
 * The subset of store state tracked by the zundo `temporal` middleware for undo/redo.
 * Only `annotations` participates in history.
 */
export type AnnotationHistoryTemporalState = Pick<AnnotationV2StoreState, 'annotations'>;

/**
 * The annotation store augmented by zundo, which hangs the undo/redo `temporal`
 * store off the `.temporal` property.
 */
export type AnnotationV2StoreApi = StoreApi<AnnotationV2StoreState> & {
  temporal: StoreApi<TemporalState<AnnotationHistoryTemporalState>>;
};

export const AnnotationV2StoreContext = createContext<AnnotationV2StoreApi | null>(null);

export type AnnotationV2ProviderProps = {
  children: ReactNode;
};

export const AnnotationV2Provider = memo(({ children }: AnnotationV2ProviderProps) => {
  const centralizedClipId = useSelector(centralizedClipIdSelector);
  const previousClipId = usePrevious(centralizedClipId);
  const storeRef = useRef<AnnotationV2StoreApi | null>(null);

  // Imperative comment-input focus coordination is ref-backed (non-reactive) rather than store state.
  const commentInputFocusHandlersRef = useRef<Partial<Record<CommentInputFocusTarget, CommentInputFocusHandler>>>({});
  const pendingCommentInputFocusTargetRef = useRef<CommentInputFocusTarget | null>(null);
  const commentInputFocusAnimationFrameRef = useRef<number | null>(null);

  if (!storeRef.current) {
    storeRef.current = createStore<AnnotationV2StoreState>()(
      temporal(
        (...args) => ({
          ...createAnnotationSessionSlice()(...args),
          ...createAnnotationHistorySlice()(...args),
          ...createAnnotationDiscussionSlice()(...args),
          ...createAnnotationCommentInputSlice({
            animationFrameRef: commentInputFocusAnimationFrameRef,
            handlersRef: commentInputFocusHandlersRef,
            pendingTargetRef: pendingCommentInputFocusTargetRef,
          })(...args),
        }),
        {
          limit: MAX_ANNOTATION_HISTORY_SIZE,
          // Only track annotations for undo/redo.
          partialize: (state): AnnotationHistoryTemporalState => ({ annotations: state.annotations }),
          // Skip recording when annotations are unchanged. This keeps unrelated slice
          // updates (e.g. selected tool) out of history and absorbs the canvas re-emitting
          // the same annotations after an undo/redo, so no manual re-entrancy guard is needed.
          equality: (pastState, currentState) => isEqual(pastState.annotations, currentState.annotations),
        },
      ),
    ) as AnnotationV2StoreApi;
  }

  // Reset all annotation state when the clip changes.
  useEffect(() => {
    if (previousClipId === centralizedClipId) {
      return;
    }

    const store = storeRef.current;
    const state = store?.getState();

    state?.resetAnnotationHistory();
    state?.resetAnnotationSession();
    state?.resetAnnotationDiscussion();
    state?.cancelPendingCommentInputFocus();

    // Wipe undo/redo history so a new clip doesn't inherit the previous clip's stack.
    store?.temporal.getState().clear();
  }, [centralizedClipId, previousClipId]);

  // Cancel any scheduled comment-input focus on unmount.
  useEffect(
    () => () => {
      storeRef.current?.getState().cancelPendingCommentInputFocus();
    },
    [],
  );

  return <AnnotationV2StoreContext.Provider value={storeRef.current}>{children}</AnnotationV2StoreContext.Provider>;
});

AnnotationV2Provider.displayName = 'AnnotationV2Provider';

export const useAnnotationV2StoreApi = (): AnnotationV2StoreApi => {
  const store = useContext(AnnotationV2StoreContext);

  if (!store) {
    const context = 'useAnnotationV2StoreApi must be used within an AnnotationV2Provider';
    const error = new Error(context);

    reportErrorToBugsnag({ context, error });
    throw error;
  }

  return store;
};

export const useAnnotationV2Store = <T,>(selector: (store: AnnotationV2StoreState) => T): T => {
  const store = useAnnotationV2StoreApi();

  return useStore(store, selector);
};

/**
 * Subscribes to the zundo `temporal` store for annotation undo/redo. Exposes
 * `undo`, `redo`, `clear`, and the `pastStates`/`futureStates` stacks.
 */
export const useAnnotationV2TemporalStore = <T,>(
  selector: (state: TemporalState<AnnotationHistoryTemporalState>) => T,
): T => {
  const store = useAnnotationV2StoreApi();

  return useStore(store.temporal, selector);
};
